'use client';

import { useEffect, useState } from 'react';
import { Pause, Play } from 'lucide-react';

import {
  isMotionPaused,
  setMotionPaused,
  MOTION_CHANGE_EVENT,
} from '@/hooks/useSafeReducedMotion';

/* Footer "Pause animations" control (session-23 P9). Most visitors never set
   their OS reduced-motion flag, so an explicit kill switch sits next to the
   theme toggle: it stores `mnrq-motion: paused`, mirrors it onto
   <html data-motion="paused"> for the CSS backstop, and every animation
   primitive picks it up through useSafeReducedMotion. */

export function PauseAnimations() {
  const [mounted, setMounted] = useState(false);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    setMounted(true);
    const current = isMotionPaused();
    setPaused(current);
    const onChange = () => setPaused(isMotionPaused());
    window.addEventListener(MOTION_CHANGE_EVENT, onChange);
    return () => window.removeEventListener(MOTION_CHANGE_EVENT, onChange);
  }, []);

  return (
    <button
      type="button"
      onClick={() => setMotionPaused(!paused)}
      aria-pressed={mounted ? paused : false}
      className="inline-flex min-h-11 w-fit items-center gap-2 rounded-md px-4 text-sm transition-colors hover:text-[var(--pub-on-ink-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pub-on-ink-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--pub-ink-surface)]"
      style={{
        border: '1px solid var(--pub-on-ink-border)',
        borderRadius: 'var(--pub-radius-pill)',
        color: 'var(--pub-on-ink-secondary)',
      }}
    >
      {mounted && paused ? (
        <Play className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
      ) : (
        <Pause className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
      )}
      {/* Label is decided post-mount so SSR and first client render agree. */}
      {mounted && paused ? 'Play animations' : 'Pause animations'}
    </button>
  );
}

export default PauseAnimations;
